import React, { useEffect, useRef } from 'react';
import { produce } from 'immer';
import { useRoflStore } from 'store/roflStore';
import { ValidationError, getValidationError } from 'util/errors';
import { IPropertyError } from 'types/rofl';

export enum InputType {
    TEXT = 'text',
    DROPDOWN = 'dropdown',
}

interface IInputErrorElementProps {
    value?: string;
    inputType: InputType;
    error?: React.ReactNode;
    propertyError: IPropertyError | undefined;
    setError: React.Dispatch<React.SetStateAction<React.ReactNode | undefined>>;
    setIsValid: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    shouldSetError?: boolean;
    fieldIsValid: boolean;
    errorClassName: string;
    identifier: string;
    entityTitle: string;
    serviceTitle?: string;
}

export const InputErrorElement = ({
    value,
    inputType,
    error,
    propertyError,
    setError,
    setIsValid,
    shouldSetError = true,
    fieldIsValid,
    errorClassName,
    identifier,
    entityTitle,
    serviceTitle,
}: IInputErrorElementProps) => {
    const shouldShowRequiredError = useRoflStore(state => state.shouldShowRequiredError);

    // since we only want this to run once (on component mount), with no dependencies,
    // we use a useRef.
    const setInitialData = useRef(() => {
        if (propertyError?.error) {
            setError(getValidationError(propertyError.error, entityTitle, serviceTitle));
            setIsValid((isValid: Record<string, boolean>) => {
                return { ...isValid, [identifier]: false };
            });
        }
    });
    useEffect(() => {
        setInitialData.current();
    }, []);

    // monitor value for error state
    useEffect(() => {
        let updatedError = error;
        if (shouldSetError && shouldShowRequiredError) {
            if (!value) {
                switch (inputType) {
                    case InputType.TEXT:
                        updatedError = getValidationError(ValidationError.GENERIC_REQUIRED, entityTitle, serviceTitle);
                        break;
                    case InputType.DROPDOWN:
                        updatedError = getValidationError(ValidationError.UNSELECTED, entityTitle, serviceTitle);
                        break;
                    default:
                        updatedError = getValidationError(ValidationError.GENERIC_REQUIRED, entityTitle, serviceTitle);
                        break;
                }
                setError(updatedError);
            }
        }

        setIsValid(produce(draft => {
            draft[identifier] = fieldIsValid && updatedError === undefined;
        }));
    }, [
        value,
        inputType,
        error,
        identifier,
        setIsValid,
        setError,
        entityTitle,
        fieldIsValid,
        serviceTitle,
        shouldSetError,
        shouldShowRequiredError,
    ]);

    if (!error) {
        return null;
    }

    return (
        <span className={`workflow-node-panel-input-error-element${errorClassName ? ` ${errorClassName}` : ''}`}>
            {error}
        </span>
    );
};
